import React, { useState, useEffect } from "react";
import { ref, set } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { X, Edit } from "lucide-react";
import { logRecordAuditEvent } from "../../../utils/recordAuditLog";
import { maskPhoneNumber } from "../../../utils/dataMasking";
import { useIsCompanyAdmin } from "../../../utils/useUserRole";

const EditClientModal = ({ client, companyName, onClose }) => {
  const [formData, setFormData] = useState({
    cloudName: "",
    location: "",
    phoneNumber: "",
    email: "",
    status: "Active",
    mode: "Credit",
    unitPrice: "",
    linkedShop: false,
  });
  const [loading, setLoading] = useState(false);
  const isCompanyAdmin = useIsCompanyAdmin(companyName);

  useEffect(() => {
    if (client) {
      setFormData({
        cloudName: client.cloudName || client.CloudName || client.name || "",
        location: client.location || client.Location || client.address || "",
        phoneNumber: client.phone || client.PhoneNumber || "",
        email: client.email || client.Email || "",
        status: client.status || "Active",
        mode: client.mode || "Credit",
        unitPrice: client.unitPrice || client.UnitPrice || "",
        linkedShop: Boolean(client.linkedShop ?? client.LinkedShop),
      });
    }
  }, [client]);

  if (!client) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cloudName.trim()) {
      toast.error("Client name is required");
      return;
    }

    setLoading(true);
    try {
      const updatedClient = {
        Id: client.recordId ?? client.Id ?? "",
        CompanyName: client.companyName || client.CompanyName || companyName || "",
        CreatedAt: client.createdAt || client.CreatedAt || "",
        OtherName: client.otherName || client.OtherName || client.id || "",
        Balance: Number(client.balance ?? client.Balance ?? 0),
        CloudName: formData.cloudName,
        Location: formData.location,
        PhoneNumber: formData.phoneNumber,
        Email: formData.email,
        Status: formData.status,
        Mode: formData.mode,
        LinkedShop: formData.linkedShop,
        PaybillNumber: client.paybill || client.PaybillNumber || "",
        UnitPrice: parseFloat(formData.unitPrice) || 0,
        UpdatedAt: new Date().toISOString(),
      };

      // Update in ValidatedClients
      await set(
        ref(database, `Companies/${companyName}/ValidatedClients/${client.id}`),
        updatedClient
      );

      await logRecordAuditEvent({
        action: "edit",
        entityType: "client",
        companyName,
        recordId: client.id || client.otherName,
        sourceComponent: "EditClientModal",
        oldData: client,
        newData: updatedClient,
      });

      // Update in AIValidatedClients
      await set(
        ref(database, `Companies/${companyName}/AIValidatedClients/${client.id}`),
        updatedClient
      );

      toast.success("Client updated successfully");
      onClose();
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Failed to update client. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
            <Edit size={24} />
            Edit Client Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Name *
              </label>
              <input
                type="text"
                name="cloudName"
                value={formData.cloudName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="text"
                name="phoneNumber"
                value={isCompanyAdmin ? formData.phoneNumber : maskPhoneNumber(formData.phoneNumber)}
                onChange={isCompanyAdmin ? handleChange : undefined}
                readOnly={!isCompanyAdmin}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${!isCompanyAdmin ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""}`}
                title={!isCompanyAdmin ? "Only CompanyAdmin can edit the phone number" : undefined}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit Price per Liter (KES)
              </label>
              <input
                type="number"
                name="unitPrice"
                step="0.01"
                min="0"
                value={formData.unitPrice}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Price per liter"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mode
                </label>
                <select
                  name="mode"
                  value={formData.mode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="Credit">Credit</option>
                  <option value="Cash">Cash</option>
                  <option value="Distributor">Distributor</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                name="linkedShop"
                checked={formData.linkedShop}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, linkedShop: e.target.checked }))
                }
                className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              Linked Shop
            </label>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:bg-cyan-300"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClientModal;
